import { useEffect, useMemo, useState } from "react";
import { api, euros } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Send, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { CAMP_STATUS, fmtDate } from "./shared";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fr = (s) => (s ? s.split("-").reverse().join("/") : "");
const pct = (a, b) => (b ? Math.round((a * 1000) / b) / 10 : 0);
const PRESETS = [["today", "Aujourd'hui", 0], ["7", "7 jours", 6], ["30", "30 jours", 29], ["90", "90 jours", 89], ["all", "Depuis le début", null]];
const CARD = "rounded-2xl border border-white/10 bg-white/[0.03] p-4";

const Delta = ({ cur, prev, invert }) => {
  if (!prev && !cur) return <p className="mt-1 text-[11px] text-muted-foreground">= période préc.</p>;
  const v = prev ? Math.round(((cur - prev) * 100) / prev) : 100;
  const up = v >= 0;
  const good = invert ? !up : up;
  return (
    <p className={`mt-1 flex items-center gap-0.5 text-[11px] ${good ? "text-emerald-400" : "text-rose-400"}`}>
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{up ? "+" : ""}{v} % <span className="ml-1 text-muted-foreground">vs {prev}</span>
    </p>
  );
};

const Kpi = ({ label, value, sub, cur, prev, invert, testid }) => (
  <div className={CARD} data-testid={testid}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-1 text-2xl font-bold tabular-nums text-white">{value}</p>
    {sub && <p className="text-[11px] text-sky-400">{sub}</p>}
    <Delta cur={cur} prev={prev} invert={invert} />
  </div>
);

const Funnel = ({ title, rows, color, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <div className={CARD} data-testid={testid}>
      <p className="mb-3 text-sm font-semibold text-white">{title}</p>
      <div className="space-y-2">
        {rows.map(([l, v, p]) => (
          <div key={l} className="grid grid-cols-[110px_1fr_60px_44px] items-center gap-2 text-xs">
            <span className="text-muted-foreground">{l}</span>
            <div className="h-2 overflow-hidden rounded-full bg-white/10"><div className={`h-full rounded-full ${color}`} style={{ width: `${(v * 100) / max}%` }} /></div>
            <span className="text-right font-semibold tabular-nums text-white">{v.toLocaleString("fr-FR")}</span>
            <span className="text-right text-muted-foreground">{p == null ? "" : `${p} %`}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const COLS = [["name", "Campagne"], ["status", "Statut"], ["contacts", "Contacts"], ["sent", "Envoyés"], ["delivered", "Délivrés"], ["opened", "Ouvertures"], ["or", "Taux ouv."], ["clicked", "Clics"], ["cr", "Taux clic"], ["registered", "Inscriptions"], ["purchased", "Achats"], ["revenue_cents", "CA"], ["unsubscribed", "Désinscr."], ["errors", "Erreurs"]];

const Compare = ({ rows, onOpen }) => {
  const [sort, setSort] = useState(["sent", -1]);
  const data = useMemo(() => rows.map((r) => ({ ...r, or: pct(r.opened, r.delivered), cr: pct(r.clicked, r.delivered) }))
    .sort((a, b) => { const x = a[sort[0]], y = b[sort[0]]; return (typeof x === "string" ? x.localeCompare(y) : (x || 0) - (y || 0)) * sort[1]; }), [rows, sort]);
  return (
    <div className={CARD} data-testid="campaign-compare">
      <p className="mb-3 text-sm font-semibold text-white">Comparatif des campagnes <span className="text-xs font-normal text-muted-foreground">· cliquez sur une colonne pour trier, sur une ligne pour le rapport détaillé</span></p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-xs">
          <thead className="text-muted-foreground"><tr>{COLS.map(([k, l]) => (
            <th key={k} className="cursor-pointer select-none p-2 hover:text-white" onClick={() => setSort([k, sort[0] === k ? -sort[1] : -1])} data-testid={`compare-sort-${k}`}>{l}{sort[0] === k ? (sort[1] < 0 ? " ↓" : " ↑") : ""}</th>
          ))}</tr></thead>
          <tbody>
            {data.map((r) => (
              <tr key={r.id} onClick={() => onOpen(r)} className="cursor-pointer border-t border-white/5 hover:bg-white/[0.04]" data-testid={`compare-row-${r.id}`}>
                <td className="p-2 font-medium text-white">{r.name}<div className="text-[10px] text-muted-foreground">{fmtDate(r.launched_at)}</div></td>
                <td className="p-2"><span className={`rounded-full px-2 py-0.5 text-[10px] ${(CAMP_STATUS[r.status] || CAMP_STATUS.draft).cls}`}>{(CAMP_STATUS[r.status] || CAMP_STATUS.draft).label}</span></td>
                <td className="p-2">{r.contacts}</td><td className="p-2">{r.sent}</td><td className="p-2">{r.delivered}</td><td className="p-2">{r.opened}</td>
                <td className="p-2">{r.or} %</td><td className="p-2">{r.clicked}</td><td className="p-2">{r.cr} %</td><td className="p-2">{r.registered}</td>
                <td className="p-2">{r.purchased}</td><td className="p-2">{euros(r.revenue_cents)}</td><td className="p-2">{r.unsubscribed}</td><td className="p-2">{r.errors}</td>
              </tr>
            ))}
            {!data.length && <tr><td colSpan={COLS.length} className="p-3 text-center text-muted-foreground">Aucune campagne.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const CampaignDashboard = ({ refreshKey, onSent, onOpenCampaign }) => {
  const today = new Date();
  const [preset, setPreset] = useState("30");
  const [range, setRange] = useState({ start: ymd(new Date(Date.now() - 29 * 864e5)), end: ymd(today) });
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  const load = () => api.get("/admin/email-campaigns/analytics", { params: range }).then((r) => { setD(r.data); setErr(false); }).catch(() => setErr(true));
  useEffect(() => { load(); }, [range, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (key, back) => {
    setPreset(key);
    if (back === null) {
      try {
        const r = await api.get("/admin/email-campaigns");
        const first = (r.data || []).map((c) => c.created_at).filter(Boolean).sort()[0];
        setRange({ start: first ? first.slice(0, 10) : ymd(today), end: ymd(today) });
      } catch { /* */ }
    } else setRange({ start: ymd(new Date(Date.now() - back * 864e5)), end: ymd(today) });
  };
  const sendNow = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/email-campaigns/send-batch"); toast.success(r.data.skipped === "busy" ? "Un envoi est déjà en cours" : "Lot lancé : les e-mails partent en arrière-plan (environ 1 minute)"); setTimeout(() => { load(); onSent?.(); }, 60000); }
    catch { toast.error("Envoi impossible"); } finally { setBusy(false); }
  };

  if (!d) return err ? (
    <div className="rounded-2xl border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-300" data-testid="dashboard-error">Statistiques indisponibles pour le moment. <button onClick={load} className="underline">Réessayer</button></div>
  ) : <Loader2 className="animate-spin text-muted-foreground" />;
  const c = d.current, p = d.previous, b = d.base;
  const hours = d.hours.map((v, h) => ({ h: `${h}h`, v }));
  return (
    <div className="space-y-3" data-testid="campaign-dashboard">
      <div className={`${CARD} flex flex-wrap items-center gap-2`}>
        {PRESETS.map(([k, l, back]) => (
          <button key={k} onClick={() => pick(k, back)} className={`rounded-full border px-3 py-1.5 text-xs ${preset === k ? "border-sky-400 bg-sky-500 text-white" : "border-white/15 text-muted-foreground hover:text-white"}`} data-testid={`period-${k}`}>{l}</button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">Du</span>
        <input type="date" value={range.start} onChange={(e) => { setPreset(""); setRange({ ...range, start: e.target.value }); }} className="h-8 rounded-md border border-white/15 bg-black/40 px-2 text-xs text-white [color-scheme:dark]" data-testid="period-start" />
        <span className="text-xs text-muted-foreground">au</span>
        <input type="date" value={range.end} onChange={(e) => { setPreset(""); setRange({ ...range, end: e.target.value }); }} className="h-8 rounded-md border border-white/15 bg-black/40 px-2 text-xs text-white [color-scheme:dark]" data-testid="period-end" />
        <Button size="sm" onClick={sendNow} disabled={busy} className="ml-auto rounded-full bg-emerald-500 text-black hover:bg-emerald-400" data-testid="campaign-send-btn">
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />} Envoyer un lot maintenant
        </Button>
        <p className="w-full text-[11px] text-muted-foreground" data-testid="period-label">Période du {fr(d.start)} au {fr(d.end)} · comparée au {fr(d.prev_start)} – {fr(d.prev_end)}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Envoyés" value={c.sent.toLocaleString("fr-FR")} cur={c.sent} prev={p.sent} testid="kpi-sent" />
        <Kpi label="Délivrés" value={c.delivered.toLocaleString("fr-FR")} sub={`${pct(c.delivered, c.sent)} % des envoyés`} cur={c.delivered} prev={p.delivered} testid="kpi-delivered" />
        <Kpi label="Ouvertures" value={c.opened.toLocaleString("fr-FR")} sub={`${pct(c.opened, c.delivered)} % des délivrés`} cur={c.opened} prev={p.opened} testid="kpi-opened" />
        <Kpi label="Clics" value={c.clicked.toLocaleString("fr-FR")} sub={`${pct(c.clicked, c.delivered)} % des délivrés`} cur={c.clicked} prev={p.clicked} testid="kpi-clicked" />
        <Kpi label="Inscriptions" value={c.registered} sub={`${pct(c.registered, c.delivered)} % des délivrés`} cur={c.registered} prev={p.registered} testid="kpi-registered" />
        <Kpi label="Achats de bougies" value={c.purchased} sub={`${pct(c.purchased, c.delivered)} % des délivrés · ${euros(c.revenue_cents)}`} cur={c.purchased} prev={p.purchased} testid="kpi-purchased" />
        <Kpi label="Erreurs / adresses invalides" value={c.errors} sub={`${pct(c.errors, c.sent)} % des envoyés`} cur={c.errors} prev={p.errors} invert testid="kpi-errors" />
        <Kpi label="Désinscriptions" value={c.unsubscribed} sub={`${pct(c.unsubscribed, c.delivered)} % des délivrés`} cur={c.unsubscribed} prev={p.unsubscribed} invert testid="kpi-unsub" />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Funnel testid="funnel-period" color="bg-sky-500" title={`Entonnoir de conversion de la période · ${pct(c.purchased, c.delivered)} % d'achats`} rows={[
          ["Envoyés", c.sent, null], ["Délivrés", c.delivered, pct(c.delivered, c.sent)], ["Ouvertures", c.opened, pct(c.opened, c.delivered)],
          ["Clics", c.clicked, pct(c.clicked, c.opened)], ["Inscriptions", c.registered, pct(c.registered, c.clicked)], ["Achats", c.purchased, pct(c.purchased, c.registered)]]} />
        <Funnel testid="funnel-base" color="bg-emerald-500" title={`Transformation de toute la base prospects · conversion ${pct(b.purchased, b.imported)} %`} rows={[
          ["Importés", b.imported, null], ["Contactés", b.contacted, null], ["Ouvertures", b.opened, null],
          ["Clics", b.clicked, null], ["Inscriptions", b.registered, null], ["Achats", b.purchased, null]]} />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className={CARD} data-testid="chart-days">
          <p className="mb-2 text-sm font-semibold text-white">Jour par jour</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={d.days}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9a9aa0" }} />
              <YAxis tick={{ fontSize: 10, fill: "#9a9aa0" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#141110", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="sent" name="Envoyés" stroke="#3b82f6" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="opened" name="Ouvertures" stroke="#22c55e" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="clicked" name="Clics" stroke="#f59e0b" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="registered" name="Inscriptions" stroke="#ec4899" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="purchased" name="Achats" stroke="#a855f7" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="unsubscribed" name="Désinscriptions" stroke="#9ca3af" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className={CARD} data-testid="chart-hours">
          <p className="mb-2 text-sm font-semibold text-white">Heures d'ouverture (heure de Paris) <span className="text-xs font-normal text-muted-foreground">· meilleure heure : <span className="text-sky-400" data-testid="best-hour">{d.best_hour == null ? "—" : `${d.best_hour}h`}</span></span></p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={hours}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="h" tick={{ fontSize: 10, fill: "#9a9aa0" }} interval={1} />
              <YAxis tick={{ fontSize: 10, fill: "#9a9aa0" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#141110", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
              <Bar dataKey="v" name="Ouvertures" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <Compare rows={d.campaigns} onOpen={(r) => onOpenCampaign?.(r.id)} />

    </div>
  );
};
